import { ThemeEvents } from "@theme/events";

/**
 * PriceHistory — keeps the Omnibus price-history disclosure in step with the selected variant.
 *
 * `product-price.js` only swaps its own `[ref="priceContainer"]` when a variant changes, so
 * anything rendered next to the price — the disclosure trigger and its modal — is left showing
 * the previous variant's figures. This element re-renders itself from the same
 * `variant:update` payload instead.
 *
 * Eligibility is decided in Liquid, so the re-rendered section simply has no `<price-history>`
 * in it when the new variant is not discounted. That is the signal to empty out and hide, and
 * a later variant that *is* discounted brings the element back.
 *
 * DOM contract:
 *   [data-block-id] — the section id, matched against the re-rendered HTML
 *   a `<dialog-component>` child owning a single `<dialog>` (rendered by `snippets/modal.liquid`)
 */
class PriceHistory extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    // Same target `ProductPrice` binds to, so both react to one dispatch.
    this.#section = this.closest(".shopify-section");
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#section = null;
  }

  get #dialogComponent() {
    return /** @type {(HTMLElement & { showDialog?: () => void, closeDialog?: () => Promise<void> }) | null} */ (
      this.querySelector("dialog-component")
    );
  }

  /** @param {Event} event */
  #handleVariantUpdate = async (event) => {
    const html = /** @type {CustomEvent} */ (event).detail?.data?.html;
    if (!html) return;

    const next = html.querySelector(`price-history[data-block-id="${this.dataset.blockId}"]`);

    // Replacing the DOM under an open <dialog> strands its top-layer entry and leaves the body
    // scroll lock that DialogComponent applied — the page would be frozen with nothing visible
    // to close. Close it properly first, and reopen once the new figures are in place.
    const wasOpen = Boolean(this.querySelector("dialog")?.open);
    if (wasOpen) await this.#dialogComponent?.closeDialog?.();

    // Emptying is enough to make the element disappear: the host is `display: contents`, so with
    // no children it contributes no box and no gap to the price row. Setting `hidden` would not
    // work anyway — the `contents` utility outranks Tailwind's `[hidden]:where(...)` rule.
    if (!next) {
      this.replaceChildren();
      return;
    }

    this.replaceChildren(...next.childNodes);

    if (wasOpen) this.#dialogComponent?.showDialog?.();
  };
}

if (!customElements.get("price-history")) {
  customElements.define("price-history", PriceHistory);
}
